import { ReactNode } from 'react'
import { Box, Typography, TypographyProps } from '@mui/material'
import { StaticImageData } from 'next/image'

import CloudflareImage from '@ecm/ui/element/CloudflareImage'
import Color from '@ecm/ui/element/Color'
import MainContainer from '@ecm/ui/element/MainContainer'

import dashboardIllustration from '/public/images/illustration/dashboard.png'

type DashboardProps = {
  steps: {
    icon: StaticImageData
    title: ReactNode
    description?: string
  }[]
  heading: string
  subHeading?: string
  illustration?: StaticImageData
  headingMaxWidth?: TypographyProps['maxWidth']
} & React.ComponentProps<typeof Box>

const Dashboard = ({
  steps,
  heading,
  subHeading,
  illustration,
  headingMaxWidth,
  ...otherProps
}: DashboardProps) => (
  <Box
    py="6em"
    textAlign={{ md: 'left', xs: 'center' }}
    sx={{
      background: {
        md: `linear-gradient(180deg, ${Color.smoke.normal} 520px, rgba(255, 255, 255, 0) 750px)`,
      },
    }}
    {...otherProps}
  >
    <MainContainer>
      <Typography variant="h3" component="h2" maxWidth={headingMaxWidth}>
        {heading}
      </Typography>
      {subHeading && (
        <Typography variant="p2" color={Color.saint.lightest} fontWeight="700">
          {subHeading}
        </Typography>
      )}
      <Box
        mt={{ md: '5em', xs: '3em' }}
        display="grid"
        gridTemplateColumns={{ md: '1fr 2fr 1fr', sm: '1fr 1fr', xs: '1fr' }}
        rowGap="2em"
        columnGap={{ sm: '2em', xs: 0 }}
        alignItems="center"
      >
        <Box
          gridColumn={{ md: '2', xs: '1 / span 2' }}
          gridRow={{ md: steps.length > 4 ? '1 / span 3' : '1 / span 2', xs: '1' }}
          justifySelf="center"
        >
          <CloudflareImage src={illustration ?? dashboardIllustration} />
        </Box>
        {steps.map(({ description, icon, title }, index) => (
          <Box
            key={index}
            textAlign={{ sm: index % 2 ? 'right' : 'left', xs: 'center' }}
            justifySelf={{ md: index % 2 ? 'right' : 'left', xs: 'center' }}
            gridRow={{ sm: 'auto', xs: index + 2 }}
          >
            <Box maxWidth={{ md: '270px', xs: '240px' }} m="0 auto">
              <CloudflareImage src={icon} />
              <Typography variant="h6" component="h3" fontWeight="400" my="0.5em">
                {title}
              </Typography>
              {description && <Typography variant="p3">{description}</Typography>}
            </Box>
          </Box>
        ))}
      </Box>
    </MainContainer>
  </Box>
)

export default Dashboard
